// Purpose: admin routing view. Active + DLQ lists, replay/discard,
// circuit breaker state + manual reset, Graph cert tile. State-changing
// actions gated on MFA via the global 428 interceptor.
//
// Phase 7.5 port: PageHeader + URL-synced Tabs (overview / active /
// dead-letter) + Card-wrapped sections + DataTable lists.

import { useCallback, useEffect, useState } from 'react';
import type { RoutingJob } from '../../api/admin-routing.ts';
import {
  discardJob,
  fetchActiveJobs,
  fetchCircuitBreakerState,
  fetchDeadLetter,
  fetchRoutingSnapshot,
  replayJob,
  resetCircuitBreaker,
} from '../../api/admin-routing.ts';
import type { RoutingCircuitBreakerSnapshot, RoutingSnapshot } from '../../api/admin-routing.ts';
import { formatDateTime } from '../../lib/format.ts';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  DataTable,
  Modal,
  Tabs,
  Textarea,
  useTabs,
  type Column,
} from '../../components/ui/index.ts';

function cbTone(state: string): 'success' | 'warning' | 'danger' {
  if (state === 'closed') return 'success';
  if (state === 'half-open') return 'warning';
  return 'danger';
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'active', label: 'Active jobs' },
  { id: 'dead-letter', label: 'Dead letter' },
];

export function Routing() {
  const { activeTab } = useTabs({ items: TABS });
  const [snapshot, setSnapshot] = useState<RoutingSnapshot | null>(null);
  const [active, setActive] = useState<RoutingJob[]>([]);
  const [dead, setDead] = useState<RoutingJob[]>([]);
  const [breaker, setBreaker] = useState<RoutingCircuitBreakerSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [discardTarget, setDiscardTarget] = useState<RoutingJob | null>(null);
  const [discardReason, setDiscardReason] = useState('');
  const [replayTarget, setReplayTarget] = useState<RoutingJob | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [working, setWorking] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [snap, act, dlq, cb] = await Promise.all([
        fetchRoutingSnapshot().catch(() => null),
        fetchActiveJobs().catch(() => []),
        fetchDeadLetter().catch(() => []),
        fetchCircuitBreakerState().catch(() => null),
      ]);
      setSnapshot(snap);
      setActive(act);
      setDead(dlq);
      setBreaker(cb);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onReplay = async (): Promise<void> => {
    if (!replayTarget) return;
    setWorking(true);
    try {
      await replayJob(replayTarget.jobId);
      setReplayTarget(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onDiscard = async (): Promise<void> => {
    if (!discardTarget) return;
    if (discardReason.trim().length < 3) {
      setError('Discard reason must be at least 3 characters.');
      return;
    }
    setWorking(true);
    try {
      await discardJob(discardTarget.jobId, discardReason.trim());
      setDiscardTarget(null);
      setDiscardReason('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onResetBreaker = async (): Promise<void> => {
    setWorking(true);
    try {
      await resetCircuitBreaker();
      setConfirmReset(false);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const jobColumns: Column<RoutingJob>[] = [
    {
      key: 'jobId',
      header: 'Job ID',
      cell: (job) => <span className="font-mono text-body-s text-fg-primary">{job.jobId}</span>,
    },
    {
      key: 'uploadId',
      header: 'Upload',
      hideOnMobile: true,
      cell: (job) => (
        <span className="font-mono text-body-s text-fg-secondary">{job.uploadId.slice(0, 8)}…</span>
      ),
    },
    {
      key: 'target',
      header: 'Target',
      hideOnMobile: true,
      cell: (job) => <span className="text-body-s text-fg-primary">{job.target}</span>,
    },
    {
      key: 'enqueued',
      header: 'Enqueued',
      hideOnMobile: true,
      cell: (job) => (
        <span className="text-body-s text-fg-muted">{formatDateTime(job.enqueuedAt)}</span>
      ),
    },
  ];

  const activeColumns: Column<RoutingJob>[] = [
    ...jobColumns,
    {
      key: 'retry',
      header: 'Retry',
      width: '100px',
      cell: (job) => <span className="text-body-s text-fg-secondary">retry {job.retryCount}</span>,
    },
  ];

  const deadColumns: Column<RoutingJob>[] = [
    ...jobColumns,
    {
      key: 'actions',
      header: '',
      width: '200px',
      cell: (job) => (
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setReplayTarget(job)}>
            Replay
          </Button>
          <Button variant="danger" size="sm" onClick={() => setDiscardTarget(job)}>
            Discard
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Routing" />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      <Tabs items={TABS} />

      {activeTab === 'overview' ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Circuit breakers</CardTitle>
              <Button variant="danger" size="sm" onClick={() => setConfirmReset(true)}>
                Reset all
              </Button>
            </CardHeader>
            {breaker ? (
              <ul className="space-y-2 text-body-m">
                {(['list', 'upload', 'mail'] as const).map((fam) => (
                  <li key={fam} className="flex items-center justify-between">
                    <span className="text-fg-primary">{fam}</span>
                    <Badge tone={cbTone(breaker[fam].state)}>
                      {breaker[fam].state} · {breaker[fam].failureCount} fails
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body-s text-fg-muted">Unavailable.</p>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Snapshot</CardTitle>
            </CardHeader>
            {snapshot ? (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-body-m">
                <dt className="text-fg-muted">Pending</dt>
                <dd className="text-fg-primary">{snapshot.pending}</dd>
                <dt className="text-fg-muted">Active</dt>
                <dd className="text-fg-primary">{snapshot.active}</dd>
                <dt className="text-fg-muted">Completed 24h</dt>
                <dd className="text-fg-primary">{snapshot.completed24h}</dd>
                <dt className="text-fg-muted">DLQ</dt>
                <dd className="text-fg-primary">{snapshot.dead}</dd>
                <dt className="text-fg-muted">Cert expiry</dt>
                <dd className="text-fg-primary">
                  {snapshot.cert ? `${snapshot.cert.daysRemaining}d` : '—'}
                </dd>
              </dl>
            ) : (
              <p className="text-body-s text-fg-muted">Unavailable.</p>
            )}
          </Card>
        </div>
      ) : null}

      {activeTab === 'active' ? (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-border-default flex items-center justify-between">
            <CardTitle>Active jobs ({active.length})</CardTitle>
          </div>
          {active.length === 0 ? (
            <p className="p-5 text-body-s text-fg-muted">No active jobs.</p>
          ) : (
            <DataTable<RoutingJob>
              columns={activeColumns}
              rows={active}
              getRowKey={(j) => j.jobId}
              caption="Active jobs"
              className="border-0 rounded-none"
            />
          )}
        </Card>
      ) : null}

      {activeTab === 'dead-letter' ? (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-border-default flex items-center justify-between">
            <CardTitle>Dead letter ({dead.length})</CardTitle>
          </div>
          {dead.length === 0 ? (
            <p className="p-5 text-body-s text-fg-muted">DLQ is empty.</p>
          ) : (
            <DataTable<RoutingJob>
              columns={deadColumns}
              rows={dead}
              getRowKey={(j) => j.jobId}
              caption="Dead-letter jobs"
              className="border-0 rounded-none"
            />
          )}
        </Card>
      ) : null}

      <Modal
        open={!!replayTarget}
        onClose={() => setReplayTarget(null)}
        title="Replay routing job"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setReplayTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={working} onClick={() => void onReplay()}>
              Replay
            </Button>
          </>
        }
      >
        Re-enqueue job <code>{replayTarget?.jobId}</code>? A fresh job ID is generated; the original
        stays in the DLQ with a &quot;replayed as …&quot; annotation.
      </Modal>

      <Modal
        open={!!discardTarget}
        onClose={() => setDiscardTarget(null)}
        title="Discard routing job"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setDiscardTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={working}
              disabled={discardReason.trim().length < 3}
              onClick={() => void onDiscard()}
            >
              Discard
            </Button>
          </>
        }
      >
        <p>
          Mark <code>{discardTarget?.jobId}</code> as permanently discarded. The queue state does
          not change; this is an audit-only decision. Required reason:
        </p>
        <Textarea
          className="mt-3"
          rows={3}
          value={discardReason}
          onChange={(e) => setDiscardReason(e.target.value)}
        />
      </Modal>

      <Modal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset circuit breakers"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={working}
              onClick={() => void onResetBreaker()}
            >
              Reset
            </Button>
          </>
        }
      >
        Force all three Graph circuit breakers back to <strong>closed</strong>. The prior state is
        recorded in an audit row.
      </Modal>
    </div>
  );
}
